/**
 * Sidebar — collapsible (240 → 56 px). Persists collapsed state per user.
 * Below `lg` it is an off-canvas drawer opened from the TopBar hamburger.
 * Nav grows release-by-release per the plan (R1 → Cockpit/Inbox/Live/Customers
 * + Subscriptions; R2 → CRM/Finance/Ops/Admin; R3+ → Sequences/Analytics/HR).
 *
 * For R0 we render the full target structure but most items link to "/"
 * (placeholder). Routes get hooked up per release.
 *
 * Icons are FontAwesome Pro 7 (duotone by default) via the Icon wrapper.
 */

import { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useIsDesktopNav } from '@/hooks/useMediaQuery'
import { useUiStore } from '@/store/ui'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'
import { Icon, type IconVariant } from '@/components/ui/icon'

interface NavItem {
  label: string
  /** FA glyph name (no `fa-` prefix). */
  icon: string
  iconVariant?: IconVariant
  /** Optional image logo (public path), rendered instead of the FA icon. */
  logo?: string
  to: string
  perm?: string
  release: 'R0' | 'R1' | 'R2' | 'R3' | 'R4' | 'R5'
}

interface NavGroup {
  label?: string
  items: NavItem[]
}

const NAV: NavGroup[] = [
  {
    items: [
      { label: 'Cockpit', icon: 'grid-2', to: '/', release: 'R1' },
      { label: 'Inbox', icon: 'inbox', to: '/inbox', release: 'R1' },
      { label: 'Live', icon: 'wave-pulse', to: '/live', perm: 'crm.read', release: 'R1' },
      { label: 'Customers', icon: 'users', to: '/customers', perm: 'customers.read', release: 'R2' },
    ],
  },
  {
    label: 'CRM',
    items: [
      { label: 'Pipeline', icon: 'diagram-project', to: '/crm/pipeline', perm: 'pipeline.read', release: 'R2' },
      { label: 'Deals', icon: 'briefcase', to: '/crm/deals', perm: 'deals.read', release: 'R2' },
      { label: 'Templates', icon: 'envelope-open-text', to: '/crm/templates', perm: 'crm.write', release: 'R3' },
      { label: 'Segments', icon: 'filter', to: '/crm/segments', perm: 'crm.read', release: 'R3' },
      { label: 'Sequences', icon: 'envelopes-bulk', to: '/crm/sequences', perm: 'crm.write', release: 'R3' },
      { label: 'Tasks', icon: 'list-check', to: '/crm/tasks', perm: 'crm.read', release: 'R3' },
      { label: 'Automations', icon: 'robot', to: '/crm/automations', perm: 'crm.write', release: 'R3' },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { label: 'Campaigns', icon: 'paper-plane', to: '/marketing/campaigns', perm: 'crm.write', release: 'R3' },
      { label: 'Segments', icon: 'filter', to: '/marketing/segments', perm: 'crm.read', release: 'R3' },
      { label: 'Templates', icon: 'envelope-open-text', to: '/marketing/templates', perm: 'crm.write', release: 'R3' },
    ],
  },
  {
    label: 'Sales',
    items: [
      { label: 'Outreach', icon: 'paper-plane', to: '/workspace', perm: 'deals.write', release: 'R2' },
      { label: 'Pricing', icon: 'tag', to: '/workspace/pricing', perm: 'services.write', release: 'R2' },
      { label: 'Discounts', icon: 'ticket', to: '/workspace/discounts', perm: 'discounts.read', release: 'R2' },
      { label: 'Referrals', icon: 'user-plus', to: '/workspace/referrals', perm: 'referrals.read', release: 'R2' },
    ],
  },
  {
    label: 'Subscriptions',
    items: [
      { label: 'All', icon: 'layer-group', to: '/subscriptions', release: 'R1' },
    ],
  },
  {
    label: 'Bots',
    items: [
      { label: 'Overview', icon: 'robot', to: '/bots', perm: 'bots.read', release: 'R2' },
      { label: 'Prices', icon: 'tags', to: '/bots/prices', perm: 'bots.read', release: 'R2' },
      { label: 'Catalogue', icon: 'book-open', to: '/bots/catalogue', perm: 'bots.read', release: 'R2' },
      { label: 'Deployments', icon: 'server', to: '/bots/deployments', perm: 'bots.read', release: 'R2' },
      { label: 'Infrastructure', icon: 'network-wired', to: '/bots/infrastructure', perm: 'bots.read', release: 'R2' },
    ],
  },
  {
    label: 'Services',
    items: [
      { label: 'Relay', icon: 'tower-broadcast', to: '/services/relay', perm: 'services.read', release: 'R2' },
      { label: 'Relay Bundles', icon: 'boxes-stacked', to: '/services/relay/bundles', perm: 'services.read', release: 'R2' },
      { label: 'Relay Nodes', icon: 'network-wired', to: '/services/relay/nodes', perm: 'services.read', release: 'R2' },
      { label: 'Relay Latency', icon: 'gauge', to: '/services/relay/latency', perm: 'services.read', release: 'R2' },
      { label: 'Bundle-Wrap', icon: 'gift', to: '/services/relay/wrap', perm: 'services.read', release: 'R2' },
      { label: 'ShredStream', icon: 'satellite-dish', to: '/services/shredstream', perm: 'services.read', release: 'R2' },
      { label: 'Jito Shreds', icon: 'bolt', to: '/services/jito-shreds', perm: 'services.read', release: 'R2' },
      { label: 'gRPC', icon: 'signal-stream', to: '/services/grpc', perm: 'services.read', release: 'R2' },
      { label: 'RPC', icon: 'network-wired', to: '/services/rpc', perm: 'services.read', release: 'R2' },
      { label: 'RPC Endpoints', icon: 'plug', to: '/services/rpc-endpoints', perm: 'services.read', release: 'R2' },
      { label: 'RPC Discovery', icon: 'radar', to: '/services/rpc-discovery', perm: 'services.read', release: 'R2' },
      { label: 'Slipstream', icon: 'rocket', iconVariant: 'solid', to: '/services/slipstream', perm: 'services.read', release: 'R2' },
      { label: 'GetShreds', icon: 'cubes', to: '/services/getshreds', perm: 'services.read', release: 'R2' },
      { label: 'Dedicated Shreds', icon: 'bolt', to: '/services/dedicated-shreds', perm: 'services.read', release: 'R2' },
      { label: 'VPS', icon: 'server', to: '/services/vps', perm: 'vps.read', release: 'R2' },
      { label: 'VPS Fleet', icon: 'cubes-stacked', to: '/services/vps/fleet', perm: 'vps.read', release: 'R2' },
      { label: 'E2E Tests', icon: 'vial', to: '/services/e2e-tests', perm: 'services.read', release: 'R2' },
      { label: 'Soltrace', icon: 'chart-line', logo: '/soltrace-logo.png', to: '/services/sol-trace', perm: 'services.read', release: 'R2' },
      { label: 'Soltrace Analytics', icon: 'chart-mixed', logo: '/soltrace-logo.png', to: '/services/sol-trace/analytics', perm: 'services.read', release: 'R2' },
    ],
  },
  {
    label: 'Analytics',
    items: [
      { label: 'Realtime', icon: 'wave-pulse', to: '/analytics/realtime', perm: 'analytics.read', release: 'R4' },
      { label: 'Pages', icon: 'file-lines', to: '/analytics/pages', perm: 'analytics.read', release: 'R4' },
      { label: 'Sources', icon: 'share-nodes', to: '/analytics/sources', perm: 'analytics.read', release: 'R4' },
      { label: 'Funnels', icon: 'chart-simple', to: '/analytics/funnels', perm: 'analytics.read', release: 'R4' },
      { label: 'Attribution', icon: 'coins', to: '/analytics/attribution', perm: 'analytics.read', release: 'R4' },
      { label: 'Search', icon: 'magnifying-glass', to: '/analytics/search', perm: 'analytics.read', release: 'R4' },
      { label: 'Heatmaps', icon: 'fire-flame-simple', to: '/analytics/heatmaps', perm: 'analytics.read', release: 'R4' },
      { label: 'Replays', icon: 'circle-play', to: '/analytics/replays', perm: 'analytics.read', release: 'R4' },
      { label: 'Visitors', icon: 'id-card', to: '/analytics/visitors', perm: 'analytics.read', release: 'R4' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { label: 'Invoices', icon: 'file-invoice-dollar', to: '/finance/invoices', perm: 'invoices.read', release: 'R2' },
      { label: 'Payments', icon: 'money-bill-wave', to: '/finance/payments', perm: 'payments.read', release: 'R2' },
      { label: 'Reports', icon: 'chart-column', to: '/finance/reports', perm: 'payments.read', release: 'R2' },
      { label: 'Wallets', icon: 'wallet', to: '/finance/wallets', perm: 'payments.read', release: 'R2' },
      { label: 'Journal', icon: 'book', to: '/finance/journal', perm: 'accounting.read', release: 'R5' },
      { label: 'Accounts', icon: 'sitemap', to: '/finance/accounts', perm: 'accounting.read', release: 'R5' },
      { label: 'Expenses', icon: 'receipt', to: '/finance/expenses', perm: 'accounting.read', release: 'R5' },
      { label: 'Statements', icon: 'file-chart-column', to: '/finance/statements', perm: 'accounting.read', release: 'R5' },
      { label: 'Reconciliation', icon: 'scale-balanced', to: '/finance/reconciliation', perm: 'accounting.read', release: 'R5' },
      { label: 'Employees', icon: 'id-badge', to: '/finance/employees', perm: 'payroll.read', release: 'R5' },
      { label: 'Payroll', icon: 'money-check-dollar', to: '/finance/payroll', perm: 'payroll.read', release: 'R5' },
    ],
  },
  {
    label: 'Ops',
    items: [
      { label: 'Services', icon: 'server', to: '/ops/services', perm: 'services.read', release: 'R2' },
      { label: 'Logs', icon: 'rectangle-terminal', to: '/ops/logs', perm: 'logs.read', release: 'R2' },
      { label: 'System', icon: 'gauge-high', to: '/ops/system', perm: 'system.read', release: 'R2' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { label: 'Users', icon: 'user-shield', to: '/admin/users', perm: 'admin.users.read', release: 'R2' },
      { label: 'Audit', icon: 'scroll', to: '/admin/audit', perm: 'audit.read', release: 'R2' },
      { label: 'Service Keys', icon: 'key', to: '/admin/service-keys', perm: 'admin.service_keys', release: 'R2' },
      { label: 'Orphan subs', icon: 'link-slash', to: '/admin/orphans', perm: 'crm.write', release: 'R2' },
      { label: 'Proxy Hub', icon: 'server', to: '/admin/proxy-hub', perm: 'admin.users.read', release: 'R2' },
    ],
  },
]

export function Sidebar() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed)
  const toggleSidebar = useUiStore((s) => s.toggleSidebar)

  return (
    <>
      {/* Docked sidebar: lg and up. */}
      <aside
        className={cn(
          'hidden lg:flex h-screen sticky top-0 border-r border-hairline bg-surface flex-col shrink-0',
          'transition-[width] duration-200 ease-out',
          collapsed ? 'w-[var(--sidebar-w-collapsed)]' : 'w-[var(--sidebar-w)]',
        )}
      >
        <SidebarBrand />
        <SidebarNav collapsed={collapsed} />

        {/* Collapse toggle */}
        <button
          type="button"
          onClick={toggleSidebar}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="h-10 border-t border-hairline flex items-center justify-center text-ink-500 hover:text-ink-900 hover:bg-surface-alt transition-colors cursor-pointer"
        >
          <Icon
            name={collapsed ? 'chevrons-right' : 'chevrons-left'}
            variant="solid"
            className="text-sm"
          />
        </button>
      </aside>

      {/* Off-canvas drawer: below lg. */}
      <MobileNavDrawer />
    </>
  )
}

function SidebarBrand({ onClose }: { onClose?: () => void }) {
  return (
    <div className="h-[var(--topbar-h)] flex items-center justify-between px-3 border-b border-hairline shrink-0">
      <NavLink
        to="/"
        className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-surface-alt min-w-0"
        aria-label="Home"
      >
        <img
          src="/favicon.png"
          alt=""
          width={24}
          height={24}
          className="h-6 w-6 shrink-0 rounded-sm"
        />
        {onClose && (
          <span className="text-sm font-semibold text-ink-900">AdminDash</span>
        )}
      </NavLink>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close navigation"
          className="h-11 w-11 -mr-1.5 rounded-md flex items-center justify-center text-ink-500 hover:text-ink-900 hover:bg-surface-alt transition-colors cursor-pointer"
        >
          <Icon name="xmark" variant="solid" className="text-base" />
        </button>
      )}
    </div>
  )
}

function SidebarNav({ collapsed, touch }: { collapsed: boolean; touch?: boolean }) {
  const { hasPerm } = useAuth()
  return (
    <nav
      aria-label="Main"
      className="flex-1 overflow-y-auto overscroll-contain clean-scroll px-2 py-3 space-y-4"
    >
      {NAV.map((group, gi) => {
        const visibleItems = group.items.filter(
          (it) => !it.perm || hasPerm(it.perm),
        )
        if (!visibleItems.length) return null
        return (
          <div key={gi}>
            {group.label && !collapsed && (
              <div className="px-2 mb-1.5 text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
                {group.label}
              </div>
            )}
            <ul className="space-y-0.5">
              {visibleItems.map((it) => (
                <li key={it.to}>
                  <NavItemLink item={it} collapsed={collapsed} touch={touch} />
                </li>
              ))}
            </ul>
          </div>
        )
      })}
    </nav>
  )
}

/**
 * Below `lg` the sidebar is an off-canvas drawer. Radix Dialog supplies the
 * focus trap, Esc-to-close, scroll lock and aria-modal; we add the scrim,
 * swipe-left-to-close and close-on-navigation.
 */
function MobileNavDrawer() {
  const open = useUiStore((s) => s.mobileNavOpen)
  const setOpen = useUiStore((s) => s.setMobileNavOpen)
  const isDesktop = useIsDesktopNav()
  const location = useLocation()

  // Close whenever the route changes (a nav link was followed) and when the
  // viewport grows into the docked layout.
  const lastPath = useRef(location.pathname)
  useEffect(() => {
    if (lastPath.current !== location.pathname) {
      lastPath.current = location.pathname
      setOpen(false)
    }
  }, [location.pathname, setOpen])
  useEffect(() => {
    if (isDesktop) setOpen(false)
  }, [isDesktop, setOpen])

  // Swipe-to-close: follow the finger leftwards, dismiss past a threshold.
  const [dragX, setDragX] = useState(0)
  const touch = useRef<{ x: number; y: number; horizontal: boolean | null } | null>(null)
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0]
    touch.current = { x: t.clientX, y: t.clientY, horizontal: null }
  }
  const onTouchMove = (e: React.TouchEvent) => {
    const st = touch.current
    if (!st) return
    const t = e.touches[0]
    const dx = t.clientX - st.x
    const dy = t.clientY - st.y
    if (st.horizontal === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      st.horizontal = Math.abs(dx) > Math.abs(dy)
    }
    if (st.horizontal) setDragX(Math.min(0, dx))
  }
  const onTouchEnd = () => {
    if (touch.current?.horizontal && dragX < -70) setOpen(false)
    touch.current = null
    setDragX(0)
  }

  return (
    <DialogPrimitive.Root open={open && !isDesktop} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="lg:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px] ah-scrim-in"
          data-testid="nav-scrim"
        />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          // Focus the panel itself rather than the close button, so opening
          // the drawer doesn't paint a focus ring; Tab still moves into it.
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            ;(e.currentTarget as HTMLElement | null)?.focus()
          }}
          tabIndex={-1}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
          style={dragX ? { transform: `translateX(${dragX}px)`, transition: 'none' } : undefined}
          className={cn(
            'lg:hidden fixed inset-y-0 left-0 z-50 w-[min(300px,85vw)] flex flex-col',
            'bg-surface border-r border-hairline shadow-[var(--shadow-popover)]',
            'pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]',
            'transition-transform duration-200 ease-out',
            'ah-drawer-in',
            'focus:outline-none',
          )}
        >
          <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>
          <SidebarBrand onClose={() => setOpen(false)} />
          <SidebarNav collapsed={false} touch />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function NavItemLink({
  item,
  collapsed,
  touch,
}: {
  item: NavItem
  collapsed: boolean
  /** Drawer mode: 44 px rows for fingers. */
  touch?: boolean
}) {
  return (
    <NavLink
      to={item.to}
      // /bots is the section's overview: its sub-pages have their own rows.
      end={item.to === '/' || item.to === '/bots'}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) => {
        // For items that share a prefix (e.g. /workspace vs /workspace/discounts),
        // check that the current path doesn't belong to a sibling nav item.
        let active = isActive
        if (active && item.to === '/workspace') {
          // /workspace/discounts and /workspace/pricing have their own nav items
          const path = window.location.pathname
          if (path.startsWith('/workspace/discounts') || path.startsWith('/workspace/pricing') || path.startsWith('/workspace/referrals')) active = false
        }
        if (active && item.to === '/services/vps') {
          // /services/vps/fleet (+ /services/vps/fleet/:server_id) has its own
          // nav item; don't keep the parent VPS row highlighted there.
          const path = window.location.pathname
          if (path.startsWith('/services/vps/fleet')) active = false
        }
        if (active && item.to === '/services/sol-trace') {
          // /services/sol-trace/analytics has its own nav item; don't keep the
          // parent SolTrace row highlighted there.
          const path = window.location.pathname
          if (path.startsWith('/services/sol-trace/analytics')) active = false
        }
        return cn(
          'relative flex items-center gap-2.5 px-2 rounded-md text-sm transition-colors',
          touch ? 'h-11 text-[15px]' : 'h-8',
          active
            ? 'bg-primary-tint text-primary-ink font-semibold before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-full before:bg-primary'
            : 'text-ink-700 hover:bg-surface-alt hover:text-ink-900',
        )
      }}
    >
      {item.logo ? (
        <img
          src={item.logo}
          alt=""
          width={16}
          height={16}
          className="h-4 w-4 shrink-0 rounded-sm"
        />
      ) : (
        <Icon
          name={item.icon}
          variant={item.iconVariant ?? 'duotone'}
          fixedWidth
          className="text-sm shrink-0"
        />
      )}
      {!collapsed && <span className="truncate">{item.label}</span>}
      {!collapsed && item.release !== 'R0' && item.release !== 'R1' && (
        <span className="ml-auto text-[9px] text-ink-300 num font-medium">
          {item.release}
        </span>
      )}
    </NavLink>
  )
}
